/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AllStatesInitials, Regions, RegionsStates } from 'services/regions';

type Scope = 'nacional' | 'regional';

interface Step2Props {
  onNext: () => void;
  onBack: () => void;

  values: {
    scope: Scope;
    servicesText: string;
    selectedStateInitials: string[];
  };

  onChange: (data: Step2Props['values']) => void;
}

const MIN_DESC_LEN = 20;

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = () => setIsMobile(mq.matches);

    onChange();
    mq.addEventListener?.('change', onChange);

    return () => mq.removeEventListener?.('change', onChange);
  }, [breakpoint]);

  return isMobile;
};

const Step2: React.FC<Step2Props> = ({ onNext, onBack, values, onChange }) => {
  const { scope, servicesText, selectedStateInitials } = values;

  const [submitted, setSubmitted] = useState(false);
  const [touchedServicesText, setTouchedServicesText] = useState(false);

  const isRegional = scope === 'regional';

  const descLen = servicesText.trim().length;
  const descriptionValid = descLen >= MIN_DESC_LEN;

  const statesValid = !isRegional || selectedStateInitials.length > 0;

  const canProceed = descriptionValid && statesValid;

  const showDescError = (touchedServicesText || submitted) && !descriptionValid;
  const showStatesError = submitted && isRegional && !statesValid;

  const isMobile = useIsMobile(768);
  const [openRegion, setOpenRegion] = useState<string | null>(null);

  const lastRegionalSelection = useRef<string[]>(isRegional ? selectedStateInitials : []);

  const handleSelectScope = (next: Scope) => {
    if (next === scope) return;

    if (next === 'nacional') {
      lastRegionalSelection.current = selectedStateInitials;
      onChange({ ...values, scope: next, selectedStateInitials: AllStatesInitials });
      return;
    }

    onChange({ ...values, scope: next, selectedStateInitials: lastRegionalSelection.current });
  };

  const toggleState = (uf: string) => {
    const nextSelected = selectedStateInitials.includes(uf)
      ? selectedStateInitials.filter((x) => x !== uf)
      : [...selectedStateInitials, uf];

    onChange({ ...values, selectedStateInitials: nextSelected });
  };

  const regionsWithStates = useMemo(() => {
    return Regions.map((region) => ({
      ...region,
      states: RegionsStates[region.value] ?? [],
    }));
  }, []);

  const isRegionChecked = (ufs: string[]) =>
    ufs.length > 0 && ufs.every((uf) => selectedStateInitials.includes(uf));

  const toggleRegion = (ufs: string[]) => {
    const regionIsChecked = isRegionChecked(ufs);

    const nextSelected = regionIsChecked
      ? selectedStateInitials.filter((uf) => !ufs.includes(uf))
      : Array.from(new Set([...selectedStateInitials, ...ufs]));

    onChange({ ...values, selectedStateInitials: nextSelected });
  };

  const handleNext = () => {
    setSubmitted(true);
    if (!canProceed) return;
    onNext();
  };

  return (
    <div className="step-content">
      <h2 className="step-title">Agora, queremos entender mais sobre os seus objetivos.</h2>
      <p className="step-label mt-2 mb-2 font-weight-light">
        Esse passo nos ajuda a te entregar conteúdos relevantes
      </p>
      <div className="green-line" />

      <div className="step-form mt-5">
        <div className="row">
          <div className="col-md-12">
            <div>
              <label className="step-label">
                Quais serviços ou produtos você quer vender ao governo? Descreva com detalhes.
              </label>
              <textarea
                id="comments"
                name="comments"
                className={`form-control ${showDescError ? 'is-invalid' : ''}`}
                rows={5}
                placeholder="Ex: Tijolos, areia, argamassa, trena, EPI"
                value={servicesText}
                onChange={(e) => onChange({ ...values, servicesText: e.target.value })}
                onBlur={() => setTouchedServicesText(true)}
              />

              <div className="d-flex justify-content-between mt-1">
                {showDescError && (
                  <small className="text-danger">Descreva com pelo menos 20 caracteres.</small>
                )}

                <small className={descLen < MIN_DESC_LEN ? 'text-muted' : 'text-success'}>
                  {descLen}/{MIN_DESC_LEN}
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="step-label mb-3">Você atuará a nível nacional ou regional?</label>

          <div className="scope-buttons d-flex mb-2 gap-2">
            <button
              type="button"
              className={`btn d-flex align-items-center mr-2 ${
                scope === 'nacional' ? 'btn-success active' : 'btn-outline-secondary'
              }`}
              onClick={() => handleSelectScope('nacional')}
            >
              <i className="material-icons mr-2">public</i>
              Nacional
            </button>

            <button
              type="button"
              className={`btn d-flex align-items-center ${
                scope === 'regional' ? 'btn-success active' : 'btn-outline-secondary'
              }`}
              onClick={() => handleSelectScope('regional')}
            >
              <i className="material-icons mr-2">place</i>
              Regional
            </button>
          </div>

          {showStatesError && (
            <small className="text-danger d-block mb-2">Selecione pelo menos 1 estado.</small>
          )}

          {isRegional && (
            <>
              {isMobile ? (
                <div className={`regions-accordion ${showStatesError ? 'has-error' : ''}`}>
                  {regionsWithStates.map((region) => {
                    const regionUFs = region.states.map((s) => String(s.uf));
                    const checked = isRegionChecked(regionUFs);
                    const isOpen = openRegion === region.value;

                    return (
                      <div key={region.value} className={`region-card ${isOpen ? 'is-open' : ''}`}>
                        <button
                          type="button"
                          className="region-card__header"
                          onClick={() =>
                            setOpenRegion((prev) => (prev === region.value ? null : region.value))
                          }
                        >
                          <span className="region-card__left">
                            <input
                              type="checkbox"
                              className="region-checkbox"
                              checked={checked}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleRegion(regionUFs);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="region-card__title">{region.label}</span>
                          </span>

                          <i className="material-icons region-card__chevron">
                            {isOpen ? 'expand_less' : 'expand_more'}
                          </i>
                        </button>

                        {isOpen && (
                          <div className="region-card__content">
                            <div className="states-grid">
                              {region.states.map((st) => {
                                const uf = String(st.uf);
                                return (
                                  <label key={uf} className="state-item">
                                    <input
                                      type="checkbox"
                                      className="state-checkbox"
                                      checked={selectedStateInitials.includes(uf)}
                                      onChange={() => toggleState(uf)}
                                    />
                                    <span className="state-label">{st.label}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  className={`regions-grid ${
                    showStatesError ? 'border border-danger rounded p-2' : ''
                  }`}
                >
                  {regionsWithStates.map((region, idx) => {
                    const regionUFs = region.states.map((s) => String(s.uf));
                    const checked = isRegionChecked(regionUFs);

                    return (
                      <div
                        key={region.value}
                        className={`region-col ${idx !== 0 ? 'with-divider' : ''}`}
                      >
                        <label className="region-title">
                          <input
                            type="checkbox"
                            className="region-checkbox"
                            checked={checked}
                            onChange={() => toggleRegion(regionUFs)}
                          />
                          <span>{region.label}</span>
                        </label>

                        <div className="region-states">
                          {region.states.map((st) => {
                            const uf = String(st.uf);

                            return (
                              <label key={uf} className="state-item">
                                <input
                                  type="checkbox"
                                  className="state-checkbox"
                                  checked={selectedStateInitials.includes(uf)}
                                  onChange={() => toggleState(uf)}
                                />
                                <span className="state-label">{st.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="step-actions between">
        <button type="button" className="btn btn-outline-secondary px-4 mr-2" onClick={onBack}>
          voltar
        </button>

        <button
          type="button"
          className="btn btn-success px-4"
          onClick={handleNext}
          disabled={!canProceed}
          title={
            !canProceed ? 'Preencha a descrição e selecione os estados (se for regional).' : ''
          }
        >
          próximo
        </button>
      </div>
    </div>
  );
};

export default Step2;
